/**
 * Mode-aware MessageService
 *
 * Subclass of PrimeNG's MessageService that suppresses error-severity toasts
 * in the brief window after a MODE-blocked mutation — demo mode (brand-level,
 * bootstrap-time) and Time travel (runtime-entered, all brands, temporal-ui
 * mode). Installed as THE MessageService on every brand: a
 * bootstrap-time isDemo() gate cannot express a runtime-entered mode
 * (formerly DemoAwareMessageService, demo-only).
 *
 * Mode-blocked mutations throw from GraphQLClient.mutate(): the mode's info
 * toast is shown, then the typed error (DemoModeError / TemporalReadOnlyError)
 * propagates. GlobalErrorHandler skips the typed errors, but the ~60
 * per-service catch blocks that surface "operation failed" red toasts run
 * BEFORE the global handler — those drown the mode's info toast unless
 * filtered here.
 *
 * Why suppress at the MessageService level (not in each catch): one
 * centralized filter vs. updating every consumer; existing catches stay
 * terse — they don't need to know about modes.
 *
 * Why a time-windowed counter (not a "currently throwing" flag): JavaScript
 * can't observe an in-flight thrown error across awaits. The setTimeout-0
 * window survives the typical sync-after-throw catch path (microtask) but
 * releases before any unrelated error could legitimately fire.
 */

import { Injectable, Injector, inject } from '@angular/core';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { DemoModeService } from './demo-mode.service';
import { TemporalContextService } from './temporal-context.service';

@Injectable()
export class ModeAwareMessageService extends MessageService {
  // Lazy: both mode services inject MessageService — eager injection here
  // would create a construction cycle through the DI graph.
  private readonly _injector = inject(Injector);
  private _demoMode?: DemoModeService;
  private _temporal?: TemporalContextService;

  private get demoMode(): DemoModeService {
    return this._demoMode ??= this._injector.get(DemoModeService);
  }

  private get temporal(): TemporalContextService {
    return this._temporal ??= this._injector.get(TemporalContextService);
  }

  private get suppressing(): boolean {
    return this.demoMode.suppressingErrors || this.temporal.suppressingErrors;
  }

  /**
   * One refusal toast per code per window. A platform refusal (rate rail, cost budget,
   * batch shape) arrives once per failed in-flight request — a burst would stack a toast
   * for every one of them, all saying the same thing. Producers thread the identity
   * EXPLICITLY as `data.refusalCode` (+ the rail's `retryAfterSeconds`); nothing here
   * sniffs message strings. The window is the advertised wait where one exists, else a
   * fixed convenience window for the permanent refusals (which advertise none).
   */
  private static readonly REFUSAL_COALESCE_WINDOW_MS = 5000;
  private readonly refusalWindowUntil = new Map<string, number>();

  override add(message: ToastMessageOptions): void {
    if (message.severity === 'error' && this.suppressing) return;
    const refusal = message.data as { refusalCode?: string; retryAfterSeconds?: number } | undefined;
    if (refusal?.refusalCode) {
      const now = Date.now();
      if (now < (this.refusalWindowUntil.get(refusal.refusalCode) ?? 0)) return;
      this.refusalWindowUntil.set(
        refusal.refusalCode,
        now + (refusal.retryAfterSeconds
          ? refusal.retryAfterSeconds * 1000
          : ModeAwareMessageService.REFUSAL_COALESCE_WINDOW_MS));
    }
    super.add(message);
  }

  override addAll(messages: ToastMessageOptions[]): void {
    // Suppression AND coalescing live in add() — every message routes through it, so a
    // future producer reaching for addAll cannot bypass the one-toast-per-code window.
    for (const message of messages) this.add(message);
  }
}
